import React from 'react';
import { CalendarView } from '../../../types/calendar';
import { formatMonthYear, formatDateForDisplay } from '../../../utils/calendarUtils';

interface CalendarHeaderProps {
  currentView: CalendarView;
  currentDate: string;
  onViewChange: (view: CalendarView) => void;
  onPrevious: () => void;
  onNext: () => void;
  onToday: () => void;
}

const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  currentView,
  currentDate,
  onViewChange,
  onPrevious,
  onNext,
  onToday
}) => {
  const getTitle = () => {
    switch (currentView) {
      case 'month':
        return formatMonthYear(currentDate);
      case 'week':
      case 'day':
        return formatDateForDisplay(currentDate);
      default:
        return '';
    }
  };

  return (
    <div style={styles.header}>
      <div style={styles.leftSection}>
        <button style={styles.todayButton} onClick={onToday}>
          Today
        </button>
        <div style={styles.navigation}>
          <button style={styles.navButton} onClick={onPrevious}>
            ‹
          </button>
          <button style={styles.navButton} onClick={onNext}>
            ›
          </button>
        </div>
        <h2 style={styles.title}>{getTitle()}</h2>
      </div>

      <div style={styles.viewButtons}>
        <button
          style={{
            ...styles.viewButton,
            ...(currentView === 'month' ? styles.activeViewButton : {})
          }}
          onClick={() => onViewChange('month')}
        >
          Month
        </button>
        <button
          style={{
            ...styles.viewButton,
            ...(currentView === 'week' ? styles.activeViewButton : {})
          }}
          onClick={() => onViewChange('week')}
        >
          Week
        </button>
        <button
          style={{
            ...styles.viewButton,
            ...(currentView === 'day' ? styles.activeViewButton : {})
          }}
          onClick={() => onViewChange('day')}
        >
          Day
        </button>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid #e0e0e0'
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  todayButton: {
    padding: '8px 16px',
    backgroundColor: 'white',
    border: '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  },
  navigation: {
    display: 'flex',
    gap: '5px'
  },
  navButton: {
    width: '36px',
    height: '36px',
    backgroundColor: 'white',
    border: '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '600',
    color: '#333'
  },
  viewButtons: {
    display: 'flex',
    gap: '0',
    border: '1px solid #ddd',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  viewButton: {
    padding: '8px 16px',
    backgroundColor: 'white',
    border: 'none',
    borderRight: '1px solid #ddd',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  },
  activeViewButton: {
    backgroundColor: '#2196F3',
    color: 'white'
  }
};

export default CalendarHeader;
